export interface DateObject {
  readonly year: number | '';
  readonly month: number | '';
  readonly day: number | '';
}

export class DateObject {
  public static copyWith(target: DateObject, source: Partial<DateObject>): DateObject {
    return {
      year: source.year ?? target.year,
      month: source.month ?? target.month,
      day: source.day ?? target.day,
    };
  }

  public static willChange(value: DateObject, change: Partial<DateObject>): boolean {
    const merged = { ...value, ...change };
    return merged.day !== value.day || merged.month !== value.month || merged.year !== value.year;
  }

  public static isValid(value: Partial<DateObject>): boolean {
    const { day = -1, month = -1, year = -1 } = value;
    if (year === '' || month === '' || day === '') {
      return false;
    }

    return day !== -1 && month !== -1 && year !== -1;
  }

  public static now(): DateObject {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
  }

  public static fromString(dateString: unknown | undefined): DateObject {
    if (dateString === '') {
      return { year: '', month: '', day: '' };
    } else if (typeof dateString === 'string') {
      const parsed = dateString
        .split('-')
        .map((value: string): number => (value.trim() === '' ? -1 : Number(value)));

      return { year: parsed[0], month: parsed[1], day: parsed[2] };
    } else {
      throw new Error(`Invalid date string: ${dateString}`);
    }
  }

  public static toISOString(dateObject: DateObject | null): string {
    if (dateObject === null || !DateObject.isValid(dateObject)) {
      return '';
    }

    const sanitize = (value: number | '', length: number): string => {
      if (value === '') {
        return '';
      } else if (value === -1) {
        return '?';
      }

      return String(value).padStart(length, '0');
    };

    return `${sanitize(dateObject.year, 4)}-${sanitize(dateObject.month, 2)}-${sanitize(dateObject.day, 2)}`;
  }
}
